import React, { useState, useMemo } from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, startOfMonth, endOfMonth, parseISO } from 'date-fns';
import { Reservation } from '../../services/types';
import ReservationCard from './ReservationCard';
import { overlapsDateRange } from '../../utils/reservationHelpers';

interface ListViewProps {
    reservations: Reservation[];
    houses: any[];
    onSelectReservation: (reservation: Reservation) => void;
}

const ListView: React.FC<ListViewProps> = ({ reservations, houses, onSelectReservation }) => {
    const [currentMonth, setCurrentMonth] = useState(new Date());

    const filteredReservations = useMemo(() => {
        const monthStart = startOfMonth(currentMonth);
        const monthEnd = endOfMonth(currentMonth);

        return reservations.filter((reservation) =>
            overlapsDateRange(reservation, monthStart, monthEnd)
        );
    }, [reservations, currentMonth]);

    const getHouseName = (houseId: string) => {
        const house = houses.find((h) => h.public_id === houseId);
        return house ? (house.name || house.address) : 'Unknown House';
    };

    const handlePrevMonth = () => {
        const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
        const today = new Date();
        const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);

        // Only allow going back if prevMonth is not before current month
        if (prevMonth >= currentMonthStart) {
            setCurrentMonth(prevMonth);
        }
    };

    const handleNextMonth = () => {
        setCurrentMonth(
            new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
        );
    };

    const handleToday = () => {
        setCurrentMonth(new Date());
    };

    // Check if we can go to previous month
    const canGoPrev = () => {
        const today = new Date();
        const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        const viewingMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
        return viewingMonth > currentMonthStart;
    };

    return (
        <div>
            {/* Month Navigator */}
            <div className="d-flex justify-content-between align-items-center mb-4 p-3 bg-white rounded shadow-sm">
                <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handlePrevMonth}
                    disabled={!canGoPrev()}
                >
                    <ChevronLeft size={18} />
                </Button>

                <div className="d-flex gap-3 align-items-center">
                    <h5 className="mb-0">{format(currentMonth, 'MMMM yyyy')}</h5>
                    <Button variant="outline-secondary" size="sm" onClick={handleToday}>
                        Today
                    </Button>
                </div>

                <Button variant="outline-primary" size="sm" onClick={handleNextMonth}>
                    <ChevronRight size={18} />
                </Button>
            </div>

            {/* Reservations List */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={currentMonth.toISOString()}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                >
                    {filteredReservations.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                            <p>No reservations for this month</p>
                        </div>
                    ) : (
                        <Row className="g-3">
                            {filteredReservations.map((reservation) => (
                                <Col key={reservation.public_id} xs={12} md={6} lg={4}>
                                    <ReservationCard
                                        reservation={reservation}
                                        houseName={getHouseName(reservation.house_id)}
                                        onClick={() => onSelectReservation(reservation)}
                                    />
                                </Col>
                            ))}
                        </Row>
                    )}
                </motion.div>
            </AnimatePresence>

            {/* Summary */}
            <div className="mt-4 p-3 bg-light rounded text-center">
                <strong>{filteredReservations.length}</strong> reservation
                {filteredReservations.length !== 1 ? 's' : ''} in {format(currentMonth, 'MMMM yyyy')}
            </div>
        </div>
    );
};

export default ListView;